import { translate } from "@/composables/translate";
import BaseModel from "./BaseModel";
import { capitalizeWords } from "@/composables/textUtils";
import DateHandler from "@/composables/handlers/DateHandler";
import Student from "./Student";
import LanguageService from "@/services/LanguageService";

/**
 * User
 */
export default class User extends BaseModel{
  userStatus = {
    ADMIN: 0,
    STUDENT: 1,
    FULL_STUDENT: 2
  }

  constructor({
    id,
    name,
    email,
    password,
    uuid,
    status,
    birthDate,
    birthCountry,
    street,
    streetNumber,
    cep,
    country,
    city,
    profilePicture,
    language,
    token,
    verified,
    student,
    created_at,
    updated_at,
    deleted_at
  }) {
    super();
    
    this.id = id || null;
    this.name = name || null;
    this.email = email || null;
    this.password = password || null;
    this.uuid = uuid || null;
    this.status = status || null;
    this.birthDate = birthDate || null;
    this.birthCountry = birthCountry || null;
    this.street = street || null;
    this.streetNumber = streetNumber || null;
    this.cep = cep || null;
    this.country = country || null;
    this.city = city || null;
    this.profilePicture = profilePicture || null;
    this.language = language || null;
    this.token = token || null;
    this.verified = verified || null;
    this.student = student || null;
    this.created_at = created_at || null;
    this.updated_at = updated_at || null;
    this.deleted_at = deleted_at || null;

    this.hydrateStudent();
  }

  /**
   * Creates a Student instance
   */
  hydrateStudent() {
    if (this.student) {
      this.student = new Student(this.student);
    }
  }

  /**
   * Checks if user is an admin
   * @returns {Boolean}
   */
  isAdmin() {
    return this.status == this.userStatus.ADMIN ? true : false;
  }

  getName() {
    return capitalizeWords(this.name);
  }

  getInitialsOfName() {
    let name = this.name;
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, 'gu');

    let initials = [...name.matchAll(rgx)] || [];

    return (
      (initials.shift()?.[1] || '') + (initials.pop()?.[1] || '')
    ).toUpperCase();
  }

  getFirstName() {
    return this.name.split(' ')[0];
  }

  /**
   * Checks if password is valid
   * @param {String|Null} password* 
   * @returns {Object{success: Boolean, message: String}}
   */
  checkPassword(password = null) {
    if (password == null)
      password = this.password;
    let response = {
      success: false,
      message: ''
    }
    if (password.length < 6) {
      response.message = translate('password is too shot, it must have more than 6 characters length', 'ucfirst');
      return response;
    }
    if (password.length > 20) {
      response.message = translate('password is too long, it must have more than 20 characters length', 'ucfirst');
      return response;
    }
    const specialCharRegex = /[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
    if (!specialCharRegex.test(password)) {
      response.message = translate("invalid password, enter a special character", 'ucfirst');
      return response;
    }
    const numberRegex = /[0-9]/;
    if (!numberRegex.test(password)) {
      response.message = translate("invalid password, enter a number", 'ucfirst');
      return response;
    }
    const upperCasaRegex = /[A-Z]/;
    if (!upperCasaRegex.test(password)) {
      response.message = translate("invalid password, enter an uppercase letter", 'ucfirst');
      return response;
    }
    const lowerCaseRegex = /[a-z]/;
    if (!lowerCaseRegex.test(password)) {
      response.message = translate("invalid password, enter a lowercase letter", 'ucfirst');
      return response;
    }
    response.success = true;
    return response;
  }

  /**
   * Checks if email is valid 
   * @param {String} email* 
   * @returns {Boolean}
   */
  checkEmail(email = null) {
    if (email == null)
      email = this.email;
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  }

  getProfileImage() {
    if (this.profilePicture) {
      return this.profilePicture.indexOf('data:image/png;base64,') == 0 ? this.profilePicture : `data:image/png;base64,${this.profilePicture}`;
    }
    return null;
  }

  getBirthDate() {
    if (this.birthDate != null) {
      return DateHandler.format(this.birthDate, 'DATE_SHORT');
    }

    return null;
  }

  getLocalizedBirthDate() {
    return this.birthDate ? DateHandler.format(this.birthDate, 'DATE_SHORT') : null;
  }

  getCountry() {
    return this.country;
  }

  async getLangIso() {
    let language = this.language;

    if (this.language) {
      const item = await LanguageService.getLanguage(this.language);
      language = item ? item.getValue().code : null;
    }


    return language ? language : 'pt';
  }

  /**
   * @returns {Boolean}
   */
  hasStudent() {
    return this.student && this.student.id;
  }
}